import { StorageKeysEnum } from "../enums";
import { decodeJwt } from "./decode-jwt";

// Observabilidade client-side (PR-05, ver docs/novas/doc_back/PR-05-hardening-integracao-observabilidade-e-go-live-frontend.md
// e docs/novas/status_integracao_backend_2026-07.md). Decisão do usuário (11/07/2026): por enquanto
// só grava no console, de forma estruturada — quando existir um endpoint/tabela de ocorrências no
// backend, trocar o `console.info`/`console.error` abaixo por uma chamada real, mantendo o mesmo
// formato de evento (facilita o swap sem precisar mexer em quem chama `trackEvent`/`trackAsync`).

export type TelemetryResult = "success" | "error";

export interface TelemetryEvent {
	action: string;
	result: TelemetryResult;
	sinisterId?: string;
	agreementId?: string;
	negotiationId?: string;
	latencyMs?: number;
	httpStatus?: number;
	code?: string;
	message?: string;
}

const generateTraceId = (): string => `trc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const getCurrentUserId = (): string | undefined => {
	try {
		const token = localStorage.getItem(StorageKeysEnum.AccessToken);
		if (!token || token === "undefined") return undefined;
		return decodeJwt(token)?.user_id;
	} catch {
		return undefined;
	}
};

export const trackEvent = (event: TelemetryEvent): void => {
	const entry = {
		traceId: generateTraceId(),
		userId: getCurrentUserId(),
		timestamp: new Date().toISOString(),
		...event,
	};

	if (event.result === "error") {
		console.error("[telemetry]", entry);
	} else {
		console.info("[telemetry]", entry);
	}
};

const now = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());

/**
 * Mede a duração de `fn` e já dispara `trackEvent` no formato certo. Cobre os dois padrões de
 * erro usados no projeto: funções que retornam `T | Error` (a maioria dos services de Sinistro)
 * e funções que lançam exceção (`SinisterFollowUpService`/`SinisterInternalFollowUpService`).
 */
export const trackAsync = async <T>(
	action: string,
	fn: () => Promise<T>,
	context?: Partial<Pick<TelemetryEvent, "sinisterId" | "agreementId" | "negotiationId">>
): Promise<T> => {
	const startedAt = now();

	try {
		const result = await fn();

		if (result instanceof Error) {
			const apiError = result as Error & { code?: string; status?: number };
			trackEvent({
				action,
				result: "error",
				latencyMs: Math.round(now() - startedAt),
				httpStatus: apiError.status,
				code: apiError.code,
				message: apiError.message,
				...context,
			});
		} else {
			trackEvent({ action, result: "success", latencyMs: Math.round(now() - startedAt), ...context });
		}

		return result;
	} catch (error) {
		const apiError = error as { message?: string; code?: string; status?: number };
		trackEvent({
			action,
			result: "error",
			latencyMs: Math.round(now() - startedAt),
			httpStatus: apiError?.status,
			code: apiError?.code,
			message: apiError?.message,
			...context,
		});
		throw error;
	}
};
